import { withStyles, Box, PulloverSelect, CURRENCY_LIST_OPTIONS } from 'vopay';
import { Title } from 'UI';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import useStorage from '@/hooks/useStorage';
import NoOperationalDefault from '../SubAccounts/NoOperationalDefault/NoOperationalDefault';
import { useDialog } from '@/providers/Dialog.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useFetchDefaultBankAccount from '@/hooks/useApi/useFetchDefaultBankAccount';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import usePostAccountBalance from '@/hooks/useApi/usePostAccountBalance';
import useFetchAccountLimits from '@/hooks/useApi/useFetchAccountLimits';

import TotalBalanceCard from './widgets/InteractiveInfo/TotalBalanceCard';
import TotalPaymentsCard from './widgets/InteractiveInfo/TotalPaymentsCard';
import TotalCollectionsCard from './widgets/InteractiveInfo/TotalCollectionsCard';
import TransactionsCard from './widgets/TransactionsCard/TransactionsCard';
import BankHolidays from './widgets/BankHolidays/BankHolidays';
import RecentTransactions from './widgets/RecentTransactions/RecentTransactions';
import AccountLimits from './widgets/AccountLimits/AccountLimits';

import { format } from 'date-fns';
import styles from './HomepageRevamp.module.scss';

const cn = withStyles(styles);

type FC = FunctionComponent;

const Homepage: FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp' });
  const { userSettings } = useUserSettingsProvider();
  const history = useHistory();
  const [defaultBankAccountResponse, getDefaultBankAccount, resetGetDefaultBankAccount] = useFetchDefaultBankAccount(
    {},
  );
  const {
    response: { SubAccountType, AccountID, LoginUserName, Success, Country },
  } = useClientData();
  const { state: locationState } = useLocation<{ showPermission?: boolean }>();
  const {
    NoOperationalDefault: { noOperationalDefaultModal, storeNoOperationalDefaultModal, clearNoOperationalDefaultModal },
  } = useStorage();
  const currency = Country === 'US' ? 'USD' : 'CAD';
  const [accountBalanceResponse, getAccountBalance] = usePostAccountBalance({});
  const [accountLimitsResponse, getAccountLimits] = useFetchAccountLimits({});

  const dialogue = useDialog();
  const alertDialog = useAlertDialog();
  const currentDate = useMemo(() => format(new Date(), 'MMMM d, yyyy'), []);

  const [activeCurrencyTab, setActiveCurrencyTab] = useState<'CAD' | 'USD'>(currency);
  const currencyOptions = {
    ...CURRENCY_LIST_OPTIONS,
  };

  useEffect(() => {
    resetGetDefaultBankAccount();
    clearNoOperationalDefaultModal();
  }, [AccountID]);

  useEffect(() => {
    if (noOperationalDefaultModal === false && SubAccountType === 'franchise') {
      storeNoOperationalDefaultModal(true);
      getDefaultBankAccount({
        Timezone: userSettings.timezone,
      });
    }
  }, [
    noOperationalDefaultModal,
    SubAccountType,
    userSettings.timezone,
    getDefaultBankAccount,
    storeNoOperationalDefaultModal,
  ]);

  useEffect(() => {
    const handleNoDefaultBank = async () => {
      if (defaultBankAccountResponse.Success) {
        const defaultBanks = Object.values(defaultBankAccountResponse.DefaultOperationalBankAccount || {}).find(
          (x) => x.IsDefault,
        );
        if (!defaultBanks) {
          const confirmation = await dialogue.open(NoOperationalDefault);
          if (confirmation) history.push('/operational-accounts');
        }
      }
    };

    handleNoDefaultBank();
  }, [defaultBankAccountResponse, dialogue, history]);

  useEffect(() => {
    if (locationState && locationState.showPermission) {
      alertDialog.caution(t('Dialogue.unauthorizedAccessTitle'), t('Dialogue.unauthorizedAccessSubTitle'), {
        confirmLabel: t('Dialogue.homeButtonLabel'),
      });
    }
  }, [locationState]);

  useEffect(() => {
    if (Success) {
      getAccountBalance({
        PortalUser: AccountID,
        Currency: activeCurrencyTab,
      });
      getAccountLimits({
        PortalUser: LoginUserName,
        Date: formatUtcDate(new Date()),
      });
    }
  }, [Success, AccountID, LoginUserName, currency, activeCurrencyTab]);

  return (
    <div className={cn('homepageRevamp')}>
      <Title>{t('title')}</Title>
      <Box mt="sm" jc="sb" ai="center">
        <Title level="5" emphasized={false} className={cn('currentDate')}>
          {t('dateLabel')}: {currentDate}
        </Title>
        <Box ai="center" gapSize="8px">
          <Title level="6" emphasized={false} className={cn('selectedCurrency')}>
            {t('selectedCurrency')}
          </Title>
          <PulloverSelect
            name="currency"
            value={activeCurrencyTab}
            onChange={(value) => {
              setActiveCurrencyTab(value);
            }}
          >
            {Object.values(currencyOptions).map((option) => (
              <PulloverSelect.Option key={option} value={option}>
                {option}
              </PulloverSelect.Option>
            ))}
          </PulloverSelect>
        </Box>
      </Box>

      <div className={cn('grid')}>
        <div className={cn('gridItem', 'totalBalance')} role="button" tabIndex={0}>
          <TotalBalanceCard accountBalanceResponse={accountBalanceResponse} />
        </div>

        <div className={cn('gridItem', 'totalPayments')} role="button" tabIndex={0}>
          <TotalPaymentsCard
            accountBalanceResponse={accountBalanceResponse}
            accountLimitsResponse={accountLimitsResponse}
          />
        </div>

        <div className={cn('gridItem', 'totalCollections')} role="button" tabIndex={0}>
          <TotalCollectionsCard
            accountBalanceResponse={accountBalanceResponse}
            accountLimitsResponse={accountLimitsResponse}
          />
        </div>

        <div className={cn('card', 'gridItem', 'transactionPerformance')}>
          <TransactionsCard currency={activeCurrencyTab} />
        </div>

        <div className={cn('card', 'gridItem', 'holidays')}>
          <BankHolidays />
        </div>

        <div className={cn('bottomRow')}>
          <div className={cn('card', 'gridItem', 'recentTransactions')}>
            <RecentTransactions currency={activeCurrencyTab} />
          </div>

          <div className={cn('card', 'gridItem', 'accountLimits')}>
            <AccountLimits
              accountBalanceResponse={accountBalanceResponse}
              accountLimitsResponse={accountLimitsResponse}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

function formatUtcDate(date: Date): string {
  const utcDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return format(utcDate, 'yyyy-MM-dd');
}

export default Homepage;
